import React, { useCallback, useState } from "react";
import { Tooltip } from "react-tooltip";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraRetro, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { buildCardData, generateCardImage } from "utils/cardGenerator";

/**
 * DraftCardButton — Generates and downloads a draft summary card PNG.
 * Uses Canvas API for reliable image generation (no DOM capture).
 */
function DraftCardButton({
  userCharacters,
  opponentCharacters,
  userUsername,
  opponentUsername,
  percentages,
  isUserBlueSide,
}) {
  const [status, setStatus] = useState("idle"); // idle | loading | error

  const generateCard = useCallback(async () => {
    if (status === "loading") return;
    setStatus("loading");

    try {
      const data = await buildCardData({
        userCharacters,
        opponentCharacters,
        userUsername,
        opponentUsername,
        percentages,
        isUserBlueSide,
      });

      const dataUrl = await generateCardImage(data);

      // Trigger download
      // eslint-disable-next-line fp/no-mutating-assign
      const link = Object.assign(document.createElement("a"), {
        download: "drafter_draft_card.png",
        href: dataUrl,
      });
      link.click();

      setStatus("idle");
    } catch (err) {
      console.error("Draft card generation failed:", err);
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  }, [
    status,
    userCharacters,
    opponentCharacters,
    userUsername,
    opponentUsername,
    percentages,
    isUserBlueSide,
  ]);

  const getTooltipText = () => {
    if (status === "error") return "Generation failed, try again";
    if (status === "loading") return "Generating...";
    return "Download draft card";
  };
  const tooltipText = getTooltipText();

  return (
    <>
      <motion.button
        className="screenButton"
        data-tooltip-id="draftCardButton"
        data-tooltip-content={tooltipText}
        whileHover={{ scale: status === "loading" ? 1 : 1.1 }}
        whileTap={{ scale: status === "loading" ? 1 : 0.9 }}
        onClick={generateCard}
        disabled={status === "loading"}
        style={status === "loading" ? { opacity: 0.7, cursor: "wait" } : {}}
      >
        <FontAwesomeIcon
          icon={status === "loading" ? faSpinner : faCameraRetro}
          spin={status === "loading"}
        />
      </motion.button>

      <Tooltip id="draftCardButton" />
    </>
  );
}

export default DraftCardButton;
